import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getLocalDayStart, getLocalWeekStart, getLocalMonthStart } from '@/lib/timezoneUtils';

interface SDRMetric {
  userId: string;
  userName: string;
  callsCount: number;
  callsDuration: number; // in seconds
}

interface PeriodMetrics {
  period: 'today' | 'week' | 'month';
  metrics: SDRMetric[];
  loading: boolean;
}

export function useSDRDashboardMetrics() {
  const [todayMetrics, setTodayMetrics] = useState<SDRMetric[]>([]);
  const [weekMetrics, setWeekMetrics] = useState<SDRMetric[]>([]);
  const [monthMetrics, setMonthMetrics] = useState<SDRMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const loadMetrics = async () => {
    try {
      // Step 1: Get SDR users from dashboard_visibility
      const { data: visibleUsers } = await supabase
        .from('dashboard_visibility')
        .select(`
          user_id,
          user:app_users!dashboard_visibility_user_id_fkey(
            id,
            display_name,
            first_name,
            last_name,
            twilio_phone_number,
            auth_user_id
          )
        `)
        .eq('dashboard_type', 'sdr')
        .eq('is_visible', true);

      const users = visibleUsers?.map(v => v.user).filter(Boolean) || [];

      if (users.length === 0) {
        console.log('No visible SDR users found in dashboard_visibility');
        setLoading(false);
        return;
      }

      // Define date ranges using timezone utilities
      const now = new Date();
      const todayStart = getLocalDayStart(now);
      const weekStart = getLocalWeekStart(now);
      const monthStart = getLocalMonthStart(now);

      // Fetch ALL completed calls using pagination
      let allCalls = [];
      let page = 0;
      const pageSize = 1000;
      let hasMore = true;
      const maxPages = 20; // Safety: max 20,000 calls

      console.log('🔄 [SDRMetrics] Starting pagination...');

      while (hasMore && page < maxPages) {
        const from = page * pageSize;
        const to = from + pageSize - 1;
        
        const { data: pageData, error: pageError } = await supabase
          .from('calls')
          .select('duration_seconds, created_at, status, direction, from_e164, to_e164, contact_id')
          .gte('created_at', monthStart.toISOString())
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
          .range(from, to);
        
        if (pageError) {
          console.error('❌ [SDRMetrics] Pagination error:', pageError);
          throw pageError;
        }
        
        if (pageData && pageData.length > 0) {
          allCalls = [...allCalls, ...pageData];
          console.log(`📄 [SDRMetrics] Page ${page + 1}: fetched ${pageData.length} calls (total: ${allCalls.length})`);
          page++;
          hasMore = pageData.length === pageSize;
        } else {
          hasMore = false;
        }
      }

      console.log('✅ [SDRMetrics] Pagination complete:', {
        totalPages: page,
        totalCalls: allCalls.length,
        period: `${monthStart.toISOString()} to now`
      });

      const calls = allCalls;

      // Create user map
      const userMap = new Map();
      users.forEach(user => {
        userMap.set(user.id, 
          user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown'
        );
      });

      // Map calls to SDRs based on Twilio numbers
      const callsWithSDR = calls?.map(call => {
        let sdrUserId = null;
        
        // Outbound: all variants
        if (call.direction === 'outbound' || call.direction === 'outbound-api' || call.direction === 'outbound-dial') {
          const sdr = users.find(u => u.twilio_phone_number === call.from_e164);
          sdrUserId = sdr?.id;
        } 
        // Inbound to client
        else if (call.direction === 'inbound' && call.to_e164?.startsWith('client:')) {
          const authUserId = call.to_e164.replace('client:', '');
          const sdr = users.find(u => u.auth_user_id === authUserId);
          sdrUserId = sdr?.id;
        }
        // Inbound to Twilio number
        else if (call.direction === 'inbound') {
          const sdr = users.find(u => u.twilio_phone_number === call.to_e164);
          sdrUserId = sdr?.id;
        }
        
        return { ...call, sdrUserId };
      }).filter(call => call.sdrUserId) || [];

      console.log('📊 [SDRMetrics] Calls mapped to SDRs:', {
        totalCalls: calls?.length,
        mappedCalls: callsWithSDR.length,
        unmappedCalls: (calls?.length || 0) - callsWithSDR.length
      });

      // Deduplication: Only count as new attempt if gap >= 2 minutes (unified with other dashboards)
      const MIN_GAP_MINUTES = 2;
      const MIN_GAP_MS = MIN_GAP_MINUTES * 60 * 1000;

      const deduplicateCallAttempts = (
        calls: Array<{ 
          created_at: string; 
          sdrUserId: string; 
          contact_id?: string; 
          to_e164?: string;
          duration_seconds?: number;
        }>
      ) => {
        const grouped = new Map<string, typeof calls>();
        
        calls.forEach(call => {
          const contactKey = call.contact_id || call.to_e164 || 'unknown';
          const key = `${call.sdrUserId}|${contactKey}`;
          
          if (!grouped.has(key)) {
            grouped.set(key, []);
          }
          grouped.get(key)!.push(call);
        });
        
        const dedupedCalls: typeof calls = [];
        
        grouped.forEach((groupCalls) => {
          const sorted = [...groupCalls].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          
          let lastCountedTime: number | null = null;
          
          sorted.forEach(call => {
            const callTime = new Date(call.created_at).getTime();
            
            if (lastCountedTime === null || (callTime - lastCountedTime) >= MIN_GAP_MS) {
              dedupedCalls.push(call);
              lastCountedTime = callTime;
            }
          });
        });
        
        return dedupedCalls;
      };

      // Apply deduplication to prevent fake counts
      const dedupedCalls = deduplicateCallAttempts(callsWithSDR);

      console.log('🔄 [SDRMetrics] Deduplication applied:', {
        originalCalls: callsWithSDR.length,
        dedupedCalls: dedupedCalls.length,
        removedFakeCalls: callsWithSDR.length - dedupedCalls.length,
        minGapMinutes: MIN_GAP_MINUTES
      });

      // Process metrics for each period
      const processMetrics = (startDate: Date): SDRMetric[] => {
        const userMetrics = new Map<string, { count: number; duration: number }>();

        dedupedCalls.forEach(call => {
          const callDate = new Date(call.created_at);
          if (callDate >= startDate && call.sdrUserId) {
            const userId = call.sdrUserId;
            const existing = userMetrics.get(userId) || { count: 0, duration: 0 };
            userMetrics.set(userId, {
              count: existing.count + 1,
              duration: existing.duration + (call.duration_seconds || 0),
            });
          }
        });

        return Array.from(userMetrics.entries())
          .map(([userId, data]) => ({
            userId,
            userName: userMap.get(userId) || 'Unknown',
            callsCount: data.count,
            callsDuration: data.duration,
          }))
          .sort((a, b) => b.callsCount - a.callsCount);
      };

      setTodayMetrics(processMetrics(todayStart));
      setWeekMetrics(processMetrics(weekStart));
      setMonthMetrics(processMetrics(monthStart));
      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Error loading SDR metrics:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();

    // Auto-refresh every 5 minutes (reduced from 30s to avoid excessive database load)
    const interval = setInterval(loadMetrics, 300000);

    return () => clearInterval(interval);
  }, []);

  return {
    todayMetrics,
    weekMetrics,
    monthMetrics,
    loading,
    lastUpdate,
    refresh: loadMetrics,
  };
}
